import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import { websiteSchema } from '@/lib/seo';
import CategoryDropdown from './components/CategoryDropdown';
import CategoryGrid from './components/CategoryGrid';
import FaqSection from './components/FaqSection';
import ArticleGrid from './components/ArticleGrid';

export default function Home() {
  const { t } = useTranslation();

  const features = [
    { icon: 'ri-layout-grid-line', title: t('text_feature_1_title'), desc: t('text_feature_1_desc') },
    { icon: 'ri-time-line', title: t('text_feature_2_title'), desc: t('text_feature_2_desc') },
    { icon: 'ri-arrow-right-line', title: t('text_feature_3_title'), desc: t('text_feature_3_desc') },
  ];

  return (
    <div className="bg-background-50">
      <JsonLd data={websiteSchema()} />
      <Navbar />

      {/* HERO */}
      <section className="relative flex min-h-[560px] w-full items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Stylish%20flat%20lay%20of%20premium%20streetwear%20including%20graphic%20tees%2C%20joggers%2C%20and%20chunky%20sneakers%20arranged%20on%20a%20warm%20terracotta%20surface%20with%20dried%20botanicals%20and%20coffee%2C%20top-down%20editorial%20fashion%20photography%2C%20golden%20hour%20tones%2C%20rich%20textures&width=1600&height=900&seq=hero-usfans-v3&orientation=landscape&nocache=true"
          alt="USFans Spreadsheet"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60"></div>

        <div className="relative z-10 mx-auto w-full max-w-4xl px-4 py-24 text-center md:px-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white backdrop-blur-sm">
            <i className="ri-sparkling-line"></i>
            {t('hero_eyebrow')}
          </span>
          <h1 className="mt-6 font-heading text-3xl font-bold leading-tight text-white md:text-5xl">
            {t('hero_h1')}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg">
            {t('hero_copy')}
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-600"
            >
              {t('main_btn_1')}
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap rounded-full bg-accent-500 px-7 py-3 text-base font-semibold text-white transition-colors hover:bg-accent-600"
            >
              {t('main_btn_2')}
            </a>
          </div>

          <p className="mt-6 flex items-center justify-center gap-2 text-sm text-white/70">
            <i className="ri-shield-check-line"></i>
            {t('hero_trust')}
          </p>
        </div>
      </section>

      {/* CATEGORY DROPDOWN */}
      <section id="spreadsheet" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-primary-700">
              <i className="ri-apps-2-line"></i>
              {t('dropdown_label')}
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t('categories_title')}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t('categories_subtitle')}
            </p>
          </div>

          <div className="mt-8 flex justify-center">
            <CategoryDropdown />
          </div>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t('categories_title')}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
              {t('categories_subtitle')}
            </p>
          </div>
          <div className="mt-10">
            <CategoryGrid />
          </div>
        </div>
      </section>

      {/* TEXT MODULE */}
      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-accent-700">
                <i className="ri-information-line"></i>
                {t('text_eyebrow')}
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                {t('text_title')}
              </h2>
              <div className="mt-5 space-y-4">
                <p className="text-sm leading-relaxed text-foreground-700 md:text-base">{t('text_p1')}</p>
                <p className="text-sm leading-relaxed text-foreground-700 md:text-base">{t('text_p2')}</p>
                <p className="text-sm leading-relaxed text-foreground-700 md:text-base">{t('text_p3')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {features.map((f) => (
                <div key={f.title} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className={`${f.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950">{f.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ARTICLE GRID — 10 articles */}
      <section id="guides" className="scroll-mt-20 bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-secondary-700">
              <i className="ri-article-line"></i>
              {t('article_entry_title')}
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t('article_entry_title')}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
              {t('article_entry_subtitle')}
            </p>
          </div>
          <div className="mt-10">
            <ArticleGrid />
          </div>
        </div>
      </section>

      {/* SAFETY */}
      <section id="safety" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-4xl px-4 text-center md:px-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-accent-700">
            <i className="ri-shield-star-line"></i>
            {t('safety_title')}
          </span>
          <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {t('safety_title')}
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {t('safety_copy')}
          </p>
          <Link
            to="/usfans-legit"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            {t('safety_cta')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="scroll-mt-20 bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t('faq_title')}
            </h2>
          </div>
          <div className="mt-10">
            <FaqSection />
          </div>
        </div>
      </section>

      {/* MAIN WEBSITE CTA */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <img
          src="https://readdy.ai/api/search-image?query=Vibrant%20overhead%20shot%20of%20colorful%20streetwear%20accessories%20and%20sneaker%20laces%20arranged%20in%20a%20geometric%20pattern%20on%20a%20rust%20orange%20background%2C%20bold%20fashion%20editorial%20photography%2C%20high%20contrast&width=1600&height=600&seq=cta-usfans-v3&orientation=landscape&nocache=true"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-primary-900/80"></div>
        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center md:px-6">
          <h2 className="font-heading text-2xl font-bold text-white md:text-4xl">{t('cta_title')}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-white/85">{t('cta_copy')}</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap rounded-full bg-white px-7 py-3 text-base font-semibold text-primary-700 transition-colors hover:bg-background-100"
            >
              {t('cta_primary')}
              <i className="ri-arrow-right-up-line ml-2"></i>
            </a>
            <Link
              to="/articles/usfans-spreadsheet-guide"
              className="whitespace-nowrap rounded-full border border-white/40 px-7 py-3 text-base font-semibold text-white transition-colors hover:bg-white/10"
            >
              {t('cta_secondary')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}